import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IArtistFilteredListParams } from 'services/artists/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateTrack,
  ITrackPageResponse,
  ITrackResponse,
  ITracksResponse,
  IUpdateTrack,
} from './types';

export const useGetTracksListInfinite = (params: IArtistFilteredListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ITracksResponse[], DefaultError>({
    queryKey: ['tracks', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Track/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ITrackPageResponse>(response, {
        groupName: `useGetTracksListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetTrack = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITrackResponse, DefaultError>({
    queryKey: ['track', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Track/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetTrack - ${id}`,
        notFoundPath: '/track',
      });
    },
    enabled: enabled,
  });
};

export const usePostTrack = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostTrack');
  return useMutation<ITrackResponse, DefaultError, ICreateTrack>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.mainTitle.title ?? i18next.t('track.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Track', body });

      return responseHandler(response, {
        groupName: `usePostTrack`,
        notFoundPath: '/track',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.mainTitle.title ?? i18next.t('track.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutTrack = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutTrack');

  return useMutation<ITrackResponse, DefaultError, IUpdateTrack>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.mainTitle.title ?? i18next.t('track.what'),
        })
      );
      const response = await apiClient({ method: 'PUT', url: 'Track', body });

      return responseHandler(response, {
        groupName: `usePutTrack`,
        notFoundPath: '/track',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.mainTitle.title ?? i18next.t('track.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteTrack = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteTrack');

  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('track.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `Track/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteTrack',
        notFoundPath: '/track',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('track.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useTrackTranscript = (TrackId: string) => {
  const { responseHandler } = useResponseHandler();
  const { dismiss } = useQueryToast('useDeleteTrack');

  return useMutation<string, DefaultError>({
    mutationFn: async () => {
      const response = await apiClient({
        method: 'PUT',
        url: `Track/Transcript`,
        params: { TrackId },
      });

      return responseHandler(response, {
        groupName: 'useDeleteTrack',
        notFoundPath: '/track',
      });
    },
    onSuccess: data => {},
    onError: dismiss,
  });
};

